// Copyright © Luware Solutions AG. All rights reserved.

import { IFilterType, MenuEntry } from '@nimbus-library/features/menu';
import { PortalPage } from '@nimbus-global/features/ui-profile/models/portal-page.enum';
import { UIProfileFilter } from '@nimbus-portal/features/ui-profile/filters/ui-profile.filter';

/** Sort key for entries with no place in the profile order — keeps them after the ordered ones. */
const NO_ORDER = Number.MAX_SAFE_INTEGER;

function pageOf(entry: MenuEntry): PortalPage | undefined {
  const descriptor = entry.filters.find(
    (candidate): candidate is IFilterType<PortalPage> => (candidate as IFilterType)?.type === UIProfileFilter,
  );
  return descriptor?.option;
}

/**
 * Reorders portal menu entries (and sub-entries) to match the UI profile `visiblePages` sequence,
 * whose array position defines the intended menu order. A grouping entry floats to the position of
 * its earliest-ordered visible child; entries not listed in the profile keep their default relative
 * order after the ordered ones. Returns the input unchanged when no profile is assigned.
 */
export function orderMenuEntriesByProfile(entries: MenuEntry[], visiblePages: PortalPage[] | null): MenuEntry[] {
  if (!visiblePages || visiblePages.length === 0) {
    return entries;
  }

  const orderByPage = new Map(visiblePages.map((page, index) => [page, index] as const));
  const orderIndexOf = (page: PortalPage | undefined): number => (page !== undefined ? (orderByPage.get(page) ?? NO_ORDER) : NO_ORDER);

  const sortKeyOf = (entry: MenuEntry): number =>
    (entry.subEntries ?? []).reduce((key, subEntry) => Math.min(key, orderIndexOf(pageOf(subEntry))), orderIndexOf(pageOf(entry)));

  return entries
    .map((entry, position) => ({
      entry: entry.subEntries?.length ? { ...entry, subEntries: orderMenuEntriesByProfile(entry.subEntries, visiblePages) } : entry,
      key: sortKeyOf(entry),
      position,
    }))
    .sort((left, right) => left.key - right.key || left.position - right.position)
    .map((wrapped) => wrapped.entry);
}
